// React Imports
import { useState, useEffect } from "react";

// External Libraries
import { Button } from "primereact/button";

// Hooks and Helpers
import { ThemeSwitcher } from "sj-ui-toolkit/src/components/ThemeSwitcher/ThemeSwitcher";
import { CodeEditorThemeSwitcher } from "sj-ui-toolkit/src/components/ThemeSwitcher/CodeEditorThemeSwitcher";
import { updateUserAttributes, fetchUserParticipationSettings } from "sj-ui-toolkit/src/api/";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { useSettingsContext } from "sj-ui-toolkit/src/atlas/contexts/Settings/SettingsContextProvider";

// Local Components
import { AnonymizationSettings } from "./AnonymizationSettings";
import { PasswordResetSection } from "./PasswordResetSection";

// Constants
import { EXTERNAL_ROUTES } from "sj-ui-toolkit/src/constants/routes";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";

export function SettingsTab() {
  useBreadcrumb({ label: "Settings", route: "/profile/settings", display: "page" });
  const { user } = useUserContext();
  const { settings } = useSettingsContext();
  const [userParticipationSettings, setUserParticipationSettings] = useState(null);
  const [isResetting, setIsResetting] = useState(false);

  const { navigateTo } = useSmartNavigation();
  const isUserSso = !!settings?.sso;

  useEffect(() => {
    const loadParticipationSettings = async () => {
      const data = await fetchUserParticipationSettings();
      setUserParticipationSettings(data);
    };

    loadParticipationSettings();
  }, []);

  const handleCheckboxChange = async (attr, checked) => {
    const previousValue = userParticipationSettings[attr];

    // Optimistically update UI
    setUserParticipationSettings(prev => ({ ...prev, [attr]: checked }));

    try {
      await updateUserAttributes(attr, checked);
    } catch (error) {
      console.error("Error updating user attributes:", error);
      setUserParticipationSettings(prev => ({ ...prev, [attr]: previousValue }));
    }
  };

  return (
    <div className="flex flex-col gap-[70px] mx-[40px]">
      {!isUserSso && (
        <PasswordResetSection
          isResetting={isResetting}
          onToggleReset={setIsResetting}
          userEmail={user?.email}
        />
      )}
      <section>
        <h2 className="atlas-h5 mb-7">Appearance</h2>
        <ThemeSwitcher />
      </section>
      <section>
        <h2 className="atlas-h5 mb-7">Code Editor Theme</h2>
        <CodeEditorThemeSwitcher />
      </section>
      <section>
        <div className="flex flex-col gap-[34px]">
          <h2 className="atlas-h5">Anonymization</h2>
          <AnonymizationSettings
            userSettings={userParticipationSettings}
            onCheckboxChange={handleCheckboxChange}
          />
        </div>
      </section>
      <section>
        <h2 className="atlas-h5">Links</h2>
        <div className="flex flex-col gap-[34px] mt-[34px]">
          <div>
            <Button
              label="Privacy Policy"
              link
              icon="pi pi-chevron-right"
              iconPos="right"
              onClick={() => navigateTo(EXTERNAL_ROUTES.privacy, true)}
              style={{ padding: 0 }}
            />
          </div>
          <div>
            <Button
              label="Terms of Use"
              link
              icon="pi pi-chevron-right"
              iconPos="right"
              onClick={() => navigateTo(EXTERNAL_ROUTES.terms, true)}
              style={{ padding: 0 }}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
